import React, { useState } from 'react';
import { Cross, Heart, UserCheck, Building2, Quote, Sparkles, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import JobTreadSection from '../components/sections/JobTreadSection';
import CTASection from '../components/sections/CTASection';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const values = [
  {
    icon: Cross,
    title: "Our Ideology",
    description: "We believe the traditional construction process can be improved. This compels our continual pursuit of developing best-in-class processes by leveraging best-in-class technology and taking a transparent and collaborative communication approach with all project stakeholders.",
    color: "bg-cyan-500",
    lightColor: "bg-cyan-50",
    textColor: "text-cyan-600"
  },
  {
    icon: Heart,
    title: "Core Values",
    description: "We are human and therefore imperfect, with no illusion of being inherently better than others. Guided by Faith, we strive to align our words and actions. Believing all we have is given, we are called to be good stewards. Simply, we aim to do what we say and do it well.",
    color: "bg-rose-500",
    lightColor: "bg-rose-50",
    textColor: "text-rose-600"
  },
  {
    icon: UserCheck,
    title: "The Client Experience",
    description: "We consider our projects a success when our client can't wait to tell their friends and family about their experience. Our team's focus is to continually learn from our experiences and hone our communication and processes to make the construction process enjoyable.",
    color: "bg-amber-500",
    lightColor: "bg-amber-50",
    textColor: "text-amber-600"
  }
];

const timeline = [
  {
    year: "2004",
    title: "Starting Down the Carved Path",
    description: "Daniel Wayne began his professional journey in the construction industry by joining his family's commercial and industrial contracting business, Wayne Brothers, Inc.",
    color: "from-slate-700 to-slate-800"
  },
  {
    year: "2008",
    title: "Establishing Career Goals",
    description: "Seeking to expand his knowledge and opportunities, Daniel earned an AAS in Construction Technology, equipping him with valuable skills for his career.",
    color: "from-slate-600 to-slate-700"
  },
  {
    year: "2012",
    title: "Setting New Heights",
    description: "Daniel managed one of the largest projects of its time—the Continental Tire Plant in Sumter, SC—featuring over 1 million square feet under roof.",
    color: "from-slate-500 to-slate-600"
  },
  {
    year: "2014",
    title: "Growth Spurred by Necessity",
    description: "Daniel gained his first commercial general contracting experience by leading the design and development of the company's 75-acre commercial business park.",
    color: "from-blue-700 to-slate-600"
  },
  {
    year: "2015",
    title: "A Dream Coming to Life",
    description: "Alongside his brother, Isaiah, Daniel becomes a shareholder of the family business as part of a strategic succession plan established by their father, Keith.",
    color: "from-blue-600 to-blue-700"
  },
  {
    year: "2017",
    title: "Technologist at Heart",
    description: "As Vice President of Technology at Wayne Brothers, Inc., Daniel spearheaded the advancement of cutting-edge technologies and optimized business processes.",
    color: "from-blue-500 to-blue-600"
  },
  {
    year: "2019",
    title: "A Pivotal Crossroad",
    description: "His spiritual journey offered new perspectives. He felt an unexpected call to leave the familiar and start anew, building his work on Christian values.",
    color: "from-cyan-600 to-blue-500"
  },
  {
    year: "2020",
    title: "New Platform Launched",
    description: "This vision gave rise to the Christ-Centered Companies (C3) platform, launching C3 Forest Products, LLC, specializing in custom high-end wood and metal furniture.",
    color: "from-cyan-500 to-cyan-600"
  },
  {
    year: "2022",
    title: "The Launch of C3 Contracting",
    description: "Deepening his connection to construction, Daniel obtained his general contractor's license and founded C3 Contracting, LLC, with a fresh Design-Build vision.",
    color: "from-cyan-400 to-cyan-500"
  }
];

const AboutPage = () => {
  const [expandedCard, setExpandedCard] = useState(null);

  const toggleCard = (index) => {
    setExpandedCard(expandedCard === index ? null : index);
  };

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-52 pb-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-96 h-96 bg-cyan-500 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-64 h-64 bg-yellow-400 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-cyan-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Building2 className="w-8 h-8 text-cyan-400" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Building with a Deeper Purpose
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-10">
            It's not what we do that defines us, but why and how we do it.
          </p>
        </div>
      </section>

      {/* Who We Are - Values Section */}
      <section className="py-24 bg-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-flex items-center bg-cyan-500/10 text-cyan-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              Who We Are
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">
              What Sets Us Apart
            </h2>
          </div>
          
          {/* Values Cards - Improved Design */}
          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div 
                key={index} 
                className="group relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-slate-100 overflow-hidden"
              >
                {/* Accent top border */}
                <div className={`absolute top-0 left-0 right-0 h-1 ${value.color}`}></div>
                
                {/* Icon */}
                <div className={`w-16 h-16 ${value.lightColor} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <value.icon className={`w-8 h-8 ${value.textColor}`} />
                </div>
                
                <h3 className="text-2xl font-bold text-slate-900 mb-4">{value.title}</h3>
                <p className="text-slate-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Section - Redesigned */}
      <section className="py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-cyan-500/5 to-transparent"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <div className="order-2 lg:order-1">
              <div className="relative">
                {/* Decorative frame */}
                <div className="absolute -inset-4 bg-gradient-to-tr from-cyan-500/20 to-amber-500/20 rounded-3xl blur-xl"></div>
                <div className="absolute inset-0 bg-gradient-to-tr from-cyan-500 to-amber-400 rounded-2xl transform rotate-2"></div>
                <img
                  src="https://customer-assets.emergentagent.com/job_c3build/artifacts/2g63ndf9_DSC00292.JPG"
                  alt="Daniel Wayne - Founder of C3 Contracting"
                  className="relative rounded-2xl shadow-2xl w-full h-[550px] object-cover object-top"
                />
                
                {/* Floating badge */}
                <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-xl p-4 hidden sm:block">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center">
                      <Sparkles className="w-6 h-6 text-cyan-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">20+ Years</p>
                      <p className="text-xs text-slate-500">Industry Experience</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Content */}
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 bg-cyan-500/10 text-cyan-400 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                Our Founder & Visionary
              </div>
              
              <h2 className="text-4xl sm:text-5xl font-bold text-white mb-8 leading-tight">
                Meet Daniel Wayne
              </h2>
              
              <div className="space-y-6 text-slate-300 leading-relaxed text-lg">
                <p>
                  Born and raised in Davidson, NC, Daniel Wayne spent the first 33 years of his life growing up in and around his family's commercial construction business. After serving in many roles the first 20 years of his professional career, he was convicted to set out on a new journey with a renewed purpose.
                </p>
                <p>
                  As his faith grew, he felt a strong conviction that God was leading him to step out of what he had known his entire life and work to build a platform where his skills could be used in a different capacity.
                </p>
                <p>
                  Upon taking a step of faith and pursuing what God had in store for him, Daniel started C3, which stands for <span className="text-cyan-400 font-semibold">'Christ-Centered-Companies'</span>. Along with his wife, Jessica, and their two children, Daniel enjoys living on a farm where they raise sheep, chickens and their two great pyrenees.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section - Interactive Journey */}
      <section className="py-24 bg-slate-50 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-flex items-center bg-cyan-500/10 text-cyan-400 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              Our History
            </span>
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
              The Inspired C3 Journey
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Through many twists and turns, the past and present have informed the journey that presses us onward into the future
            </p>
          </div>
          
          {/* Timeline Navigation - Year Buttons */}
          <div className="flex justify-center gap-2 sm:gap-4 mb-12 flex-wrap">
            {timeline.map((item, index) => (
              <button
                key={index}
                onClick={() => toggleCard(index)}
                className={`relative group transition-all duration-300 ${
                  expandedCard === index ? 'scale-110 z-10' : 'hover:scale-105'
                }`}
              >
                <div className={`w-14 h-14 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center shadow-lg ${
                  expandedCard === index ? 'ring-4 ring-cyan-500/50 shadow-2xl shadow-cyan-500/20' : 'opacity-70 hover:opacity-100'
                } transition-all duration-300`}>
                  <span className="text-white font-bold text-xs sm:text-base">{item.year}</span>
                </div>
              </button>
            ))}
          </div>
          
          {/* Progress Bar */}
          <div className="relative h-2 bg-slate-200 rounded-full mb-12 mx-4">
            <div 
              className="absolute top-0 left-0 h-full bg-gradient-to-r from-slate-500 via-blue-500 to-cyan-500 rounded-full transition-all duration-500"
              style={{ width: expandedCard !== null ? `${((expandedCard + 1) / timeline.length) * 100}%` : '0%' }}
            ></div>
            {timeline.map((_, index) => (
              <div 
                key={index}
                className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-2 border-white transition-all duration-300 ${
                  expandedCard !== null && index <= expandedCard 
                    ? 'bg-cyan-500 shadow-lg shadow-cyan-400/50' 
                    : 'bg-slate-300'
                }`}
                style={{ left: `${(index / (timeline.length - 1)) * 100}%`, transform: 'translate(-50%, -50%)' }}
              ></div>
            ))}
          </div>
          
          {/* Expanded Content */}
          <div className={`transition-all duration-500 ${expandedCard !== null ? 'opacity-100' : 'opacity-0'}`}>
            {expandedCard !== null && (
              <div className="bg-white rounded-3xl p-8 sm:p-12 shadow-xl border border-slate-200">
                <div className="flex flex-col items-center text-center max-w-3xl mx-auto">
                  {/* Year Badge */}
                  <span className={`inline-block px-6 py-2 rounded-full bg-gradient-to-r ${timeline[expandedCard].color} text-white text-lg font-bold mb-6 shadow-lg`}>
                    {timeline[expandedCard].year}
                  </span>
                  
                  {/* Title */}
                  <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6 leading-tight">
                    {timeline[expandedCard].title}
                  </h3>
                  
                  {/* Description */}
                  <p className="text-xl sm:text-2xl text-slate-600 leading-relaxed">
                    {timeline[expandedCard].description}
                  </p>
                </div>
                
                {/* Navigation Arrows */}
                <div className="flex justify-center gap-4 mt-10">
                  <button
                    onClick={() => expandedCard > 0 && setExpandedCard(expandedCard - 1)}
                    disabled={expandedCard === 0}
                    className={`px-8 py-3 rounded-full font-semibold transition-all ${
                      expandedCard === 0 
                        ? 'bg-slate-100 text-slate-400 cursor-not-allowed' 
                        : 'bg-slate-900 text-white hover:bg-slate-800'
                    }`}
                  >
                    ← Previous
                  </button>
                  <button
                    onClick={() => expandedCard < timeline.length - 1 && setExpandedCard(expandedCard + 1)}
                    disabled={expandedCard === timeline.length - 1}
                    className={`px-8 py-3 rounded-full font-semibold transition-all ${
                      expandedCard === timeline.length - 1 
                        ? 'bg-slate-100 text-slate-400 cursor-not-allowed' 
                        : 'bg-cyan-600 text-white hover:bg-cyan-700'
                    }`}
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </div>
          
          {/* Initial State Message */}
          {expandedCard === null && (
            <div className="text-center py-12">
              <div className="bg-white rounded-2xl shadow-lg p-8 inline-block">
                <div className="flex items-center gap-3 text-slate-600 text-lg">
                  <span className="text-2xl">👆</span>
                  <span>Select a year above to explore that chapter</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Why JobTread - with visual separator */}
      <div className="bg-gradient-to-r from-cyan-600 to-cyan-700 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-2xl sm:text-3xl font-bold text-white mb-2">
            Discover How We Deliver an Exceptional Client Experience
          </p>
          <p className="text-cyan-100 text-lg">
            Powered by industry-leading project management technology
          </p>
        </div>
      </div>
      <JobTreadSection />

      <CTASection />
      <Footer />
    </div>
  );
};

export default AboutPage;
